/**
 * Sentry Initialization
 * Centralized Sentry setup with best practices
 */

import * as Sentry from "@sentry/react";
import { SentrySpanProcessor, SentrySampler } from "@sentry/opentelemetry";
import { browserTracingIntegration, replayIntegration } from "@sentry/react";
import type { SentryConfig } from "./config";
import {
  AuthorizationError,
  NotFoundError,
  ValidationError,
  isExpectedSupportTicketHttpClientError,
  isExpectedTicketRecordNotFound,
  isExpectedTicketSaveError,
} from "@/lib/api/errors";

/**
 * Common errors that should be ignored (not actionable)
 */
const IGNORED_ERRORS = [
  // Browser extension errors
  "top.GLOBALS",
  "originalCreateNotification",
  "canvas.contentDocument",
  "MyApp_RemoveAllHighlights",
  "atomicFindClose",
  // Network errors that are not actionable
  "NetworkError",
  "Network request failed",
  "Failed to fetch",
  // Old SPA tab after a deploy: hashed chunk URLs no longer exist (hard refresh fixes)
  "Failed to fetch dynamically imported module",
  // Abort errors (expected when components unmount or requests are cancelled)
  "AbortError",
  "signal is aborted",
  "signal is aborted without reason",
  "Request was cancelled",
  "canceled",
  "ERR_CANCELED",
  "CanceledError",
  // ResizeObserver errors (common browser quirk, not actionable)
  "ResizeObserver loop limit exceeded",
  "ResizeObserver loop completed with undelivered notifications",
  // Chrome extension errors
  "chrome-extension://",
  "moz-extension://",
] as const;

/**
 * URLs that should be denied from error tracking
 */
const DENIED_URLS = [
  // Browser extensions
  /extensions\//i,
  /^chrome:\/\//i,
  /^chrome-extension:\/\//i,
  /^moz-extension:\/\//i,
  /^safari-extension:\/\//i,
  // Browser internals
  /^about:/i,
] as const;

function eventRequestUrl(event: Sentry.ErrorEvent): string {
  return (
    event.request?.url ??
    (event.contexts?.response as { url?: string } | undefined)?.url ??
    ""
  );
}

function eventHttpStatus(event: Sentry.ErrorEvent): number | undefined {
  const fromContext = (event.contexts?.response as { status_code?: number } | undefined)
    ?.status_code;
  if (typeof fromContext === "number") return fromContext;

  const msg = event.exception?.values?.map((v) => v.value).join(" ") ?? "";
  const match = msg.match(/status code:\s*(\d{3})/i);
  return match ? Number(match[1]) : undefined;
}

function shouldDropHttpClientError(event: Sentry.ErrorEvent): boolean {
  const msg = event.exception?.values?.map((v) => v.value).join(" ") ?? "";
  if (!/HTTP Client Error with status code:/i.test(msg)) {
    return false;
  }

  const url = eventRequestUrl(event);
  const status = eventHttpStatus(event);

  if (status === 403 || status === 404) {
    return true;
  }

  if (isExpectedSupportTicketHttpClientError(url, status)) {
    return true;
  }

  if (status === 404 && /\/crm-records\/records\/\d+/i.test(url)) {
    return true;
  }

  return false;
}

/**
 * Sanitize error event before sending to Sentry
 * Removes sensitive data and filters unwanted errors
 */
const sanitizeEvent = (event: Sentry.ErrorEvent, hint: Sentry.EventHint): Sentry.ErrorEvent | null => {
  if (shouldDropHttpClientError(event)) {
    return null;
  }

  if (hint.originalException instanceof AuthorizationError) {
    return null;
  }

  if (
    hint.originalException instanceof NotFoundError ||
    isExpectedTicketRecordNotFound(hint.originalException)
  ) {
    return null;
  }

  if (
    hint.originalException instanceof ValidationError &&
    isExpectedTicketSaveError(hint.originalException)
  ) {
    return null;
  }

  const exceptionMessage = event.exception?.values?.[0]?.value ?? '';
  if (
    exceptionMessage.includes('Error in handleActionButton') &&
    /ticket not found|Invalid request data/i.test(exceptionMessage)
  ) {
    return null;
  }

  // Filter ignored errors
  if (event.exception) {
    const values = event.exception.values ?? [];
    for (const entry of values) {
      const errorMessage = entry?.value || '';
      const errorType = entry?.type || '';

      if (errorType === 'NotFoundError' || /status code: 404/i.test(errorMessage)) {
        return null;
      }

      if (errorType === 'ValidationError' && /ticket not found/i.test(errorMessage)) {
        return null;
      }

      if (/status code: 400/i.test(errorMessage) && /save-and-continue|update-call-status/i.test(eventRequestUrl(event))) {
        return null;
      }

      if (IGNORED_ERRORS.some((ignored) => errorMessage.includes(ignored))) {
        return null;
      }

      if (errorType.includes('Abort') || errorType.includes('Cancel')) {
        return null;
      }
    }

    const errorMessage = values[0]?.value || '';
    const errorType = values[0]?.type || '';
    
    // Check for abort-related error codes
    if (hint.originalException && typeof hint.originalException === 'object') {
      const originalError = hint.originalException as any;
      if (
        originalError.code === 'ERR_CANCELED' || 
        originalError.name === 'AbortError' || 
        originalError.name === 'CanceledError' ||
        originalError.message?.includes('aborted') ||
        originalError.message?.includes('canceled')
      ) {
        return null; // Drop the event
      }
    }
  }

  // Console / HTTP integrations sometimes stringify as "HTTP Client Error with status code: 403"
  if (event.exception?.values?.[0]?.value?.includes('status code: 403')) {
    return null;
  }

  if (event.exception?.values?.[0]?.value?.includes('status code: 404')) {
    return null;
  }

  if (event.exception?.values?.[0]?.value?.includes('status code: 400')) {
    const url = eventRequestUrl(event);
    if (/save-and-continue|update-call-status/i.test(url)) {
      return null;
    }
  }

  // Filter by URL
  if (event.request?.url) {
    if (DENIED_URLS.some(pattern => pattern.test(event.request!.url!))) {
      return null; // Drop the event
    }
  }

  // Remove sensitive data from user context if needed
  // This is a placeholder - customize based on your needs
  if (event.user) {
    // Don't send full user objects, only IDs
    event.user = {
      id: event.user.id,
      // Explicitly exclude sensitive fields
    };
  }

  return event;
};

/**
 * Initialize Sentry with replay functionality
 * 
 * @param config - Sentry configuration object
 * @throws {Error} If DSN is invalid
 */
export function initSentry(config: SentryConfig): void {
  const {
    dsn,
    environment,
    enableReplay,
    replaySampleRate,
    errorSampleRate,
    tracesSampleRate,
    replayMaskAllText,
    replayBlockAllMedia,
    release,
  } = config;

  // Validate DSN format (basic validation)
  // DSN format: https://<key>@<host>/<project-id>
  if (!dsn.startsWith('https://') || !dsn.includes('@')) {
    throw new Error('Invalid Sentry DSN format. Expected format: https://<key>@<host>/<project-id>');
  }

  const integrations: Array<
    | ReturnType<typeof Sentry.browserTracingIntegration>
    | ReturnType<typeof Sentry.replayIntegration>
    | ReturnType<typeof Sentry.captureConsoleIntegration>
    | ReturnType<typeof Sentry.httpClientIntegration>
  > = [
    // Standard error tracking integrations
    // Note: Global error and unhandled rejection tracking are enabled by default
    Sentry.captureConsoleIntegration({
      levels: ['error'], // Only capture console.error, not warnings/info
    }),
    Sentry.httpClientIntegration({
      // Omit 400/403/404 on expected client paths; validation + stale tickets are handled in UI
      failedRequestStatusCodes: [
        [405, 599],
      ],
      failedRequestTargets: [/.*/],
    }),
    // Performance monitoring
    Sentry.browserTracingIntegration({
      // Trace navigation
      enableInp: true, // Interaction to Next Paint
    }),
  ];

  // Add replay integration if enabled
  if (enableReplay) {
    integrations.push(
      Sentry.replayIntegration({
        // Privacy settings - configurable via env vars
        maskAllText: replayMaskAllText,
        blockAllMedia: replayBlockAllMedia,
        // Performance settings
        networkDetailAllowUrls: [
          // Only capture network details for your API endpoints
          window.location.origin,
        ],
        networkCaptureBodies: false, // Don't capture request/response bodies
      })
    );
  }

  Sentry.init({
    dsn,
    environment,
    release,
    
    // Integrations
    integrations,
    
    // Performance Monitoring
    tracesSampleRate,
    
    // Session Replay Configuration
    replaysSessionSampleRate: enableReplay ? replaySampleRate : 0,
    replaysOnErrorSampleRate: enableReplay ? errorSampleRate : 0,
    
    // Event processing
    beforeSend: sanitizeEvent,
    
    // Error filtering
    ignoreErrors: [
      ...IGNORED_ERRORS,
      /^AuthorizationError/i,
      /^NotFoundError/i,
      /HTTP Client Error with status code: 404/i,
      /status code: 404/i,
      // AbortError patterns
      /^AbortError/i,
      /signal is aborted/i,
      /ERR_CANCELED/i,
      /CanceledError/i,
      /Request was cancelled/i,
    ],
    denyUrls: [...DENIED_URLS],
    
    // Additional options
    sendDefaultPii: false, // Don't send PII by default
    
    // Debug mode (only in development)
    debug: environment === 'development' && import.meta.env.VITE_SENTRY_DEBUG === 'true',
  });

  // Inside the initSentry function, after Sentry.init({...})
(window as any).Sentry = Sentry;

  // Log initialization (only in development)
  if (environment === 'development') {
    console.log('[Sentry] Initialized', {
      environment,
      replayEnabled: enableReplay,
      replaySampleRate,
      errorSampleRate,
      tracesSampleRate,
    });
  }
}

/**
 * Set user context for Sentry
 * Use this when user logs in
 */
export function setSentryUser(user: {
  id: string;
  email?: string;
  username?: string;
}): void {
  const userContext = {
    id: user.id,
    email: user.email,
    username: user.username,
  };
  
  Sentry.setUser(userContext);
  
  // Log in development to verify user context is being set
  if (import.meta.env.MODE === 'development') {
    console.log('[Sentry] User context set:', userContext);
  }
}

/**
 * Clear user context
 * Use this when user logs out
 */
export function clearSentryUser(): void {
  Sentry.setUser(null);
  
  // Log in development to verify user context is being cleared
  if (import.meta.env.MODE === 'development') {
    console.log('[Sentry] User context cleared');
  }
}

/**
 * Add breadcrumb for debugging
 */
export function addBreadcrumb(
  message: string,
  category?: string,
  level?: Sentry.SeverityLevel
): void {
  Sentry.addBreadcrumb({
    message,
    category: category || 'custom',
    level: level || 'info',
    timestamp: Date.now() / 1000,
  });
}

// Re-export Sentry for convenience
export { Sentry };

